import { Button, VStack } from "@chakra-ui/react"
import { Box } from "@chakra-ui/react/box"
import { Flex } from "@chakra-ui/react/flex"
import { Grid } from "@chakra-ui/react/grid"
import dayjs from "dayjs"
import { domAnimation, LazyMotion, m } from "framer-motion"
import { useRouter } from "next/navigation"
import { FC, useEffect, useMemo, useReducer, useSyncExternalStore } from "react"

import type { HomeWorkItem } from "@/types/getHome"

import BlurReveal from "@/components/Animations/BlurReveal"
import FeaturedWorkCard from "@/components/Cards/FeaturedWorkCard"
import ErrorMessage from "@/components/ErrorMessage"
import Inner from "@/components/Inner"
import PaginationBar from "@/components/Pagination/Pagination"
import NextLink from "@/components/RouteTransitionLink"
import { Category, Post, Skill } from "@/types/database"
import {
  generateMetaDescription,
  generatePageTitle,
  generateWorkFilterUrl,
} from "@/utils/urlHelpers"

import LoadingArticles from "./LoadingArticles"
import WorkFilters from "./WorkFilters"

const HIDDEN_CATEGORY_DEFAULT = "case-study"

export interface MyArticlesProps {
  posts: Post[]
  allTags: Skill[]
  allCategories: Category[]
  totalPages: number
  totalPosts: number
  currentPage: number
  itemsPerPage: number
  hideCategories?: boolean
  postCardName?:
    | "card3"
    | "card4"
    | "card7"
    | "card9"
    | "card10"
    | "card11"
    | "card14"
  articlePathPrefix?: "/article" | "/case-study"
  heading?: string
  initialSelectedCategories?: string[]
  initialSelectedTags?: string[]
  initialSorting?: string
}

interface FilterState {
  selectedTags: string[]
  selectedCategories: string[]
  sortingType: string
  isFilterChanging: boolean
}

type FilterAction =
  | {
      type: "sync-from-url"
      tags: string[]
      categories: string[]
      sorting: string
    }
  | { type: "start-filter-change" }

function filterReducer(state: FilterState, action: FilterAction): FilterState {
  switch (action.type) {
    case "sync-from-url":
      return {
        isFilterChanging: false,
        selectedCategories: action.categories,
        selectedTags: action.tags,
        sortingType: action.sorting,
      }
    case "start-filter-change":
      return { ...state, isFilterChanging: true }
    default:
      return state
  }
}

const filterInitialState: FilterState = {
  isFilterChanging: false,
  selectedCategories: [],
  selectedTags: [],
  sortingType: "recent",
}

const workArticlesTransition = {
  default: "none",
  "work-filter": "work-content-update",
  "work-page-back": "work-content-back",
  "work-page-forward": "work-content-forward",
} as const

const MyArticlesContent: FC<MyArticlesProps> = ({
  allCategories = [] as Category[],
  allTags = [] as Skill[],
  articlePathPrefix = "/article",
  currentPage = 1,
  hideCategories = false,
  initialSelectedCategories = [],
  initialSelectedTags = [],
  initialSorting = "recent",
  itemsPerPage = 6,
  posts = [] as Post[],
  totalPages = 0,
  totalPosts = 0,
}) => {
  const [filterState, dispatchFilter] = useReducer(filterReducer, {
    ...filterInitialState,
    selectedCategories: initialSelectedCategories,
    selectedTags: initialSelectedTags,
    sortingType: initialSorting,
  })
  const isClientReady = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  )
  const { isFilterChanging, selectedCategories, selectedTags, sortingType } =
    filterState
  const { push } = useRouter()

  const availableTags = allTags
  const availableCategories = allCategories
  const defaultCategories = useMemo(
    () => (hideCategories ? [HIDDEN_CATEGORY_DEFAULT] : []),
    [hideCategories]
  )

  // Update URL when selected filters change
  const updateUrlWithFilters = (
    tags: string[],
    categories: string[],
    sorting?: string,
    options: { skipTransition?: boolean } = {}
  ) => {
    // console.info('updateUrlWithFilters called with:', {
    //   tags,
    //   categories,
    //   sorting,
    // });

    // Preserve existing pagination parameters
    const currentParams = new URLSearchParams(window.location.search)
    const page = currentParams.get("page")
    const perPage = currentParams.get("perPage")

    const urlCategories = hideCategories ? [] : categories
    const newUrl = generateWorkFilterUrl(
      tags,
      urlCategories,
      sorting || sortingType
    )

    // Add pagination parameters back if they exist
    const urlWithPagination = new URL(newUrl, window.location.origin)
    if (page) {
      urlWithPagination.searchParams.set("page", page)
    }
    if (perPage) {
      urlWithPagination.searchParams.set("perPage", perPage)
    }

    const finalUrl = urlWithPagination.pathname + urlWithPagination.search
    console.info("Updating URL to:", finalUrl)

    // Update browser history without scroll
    push(finalUrl, {
      scroll: false,
      transitionTypes: options.skipTransition ? [] : ["work-filter"],
    })

    // Update page title and meta description to reflect current filters
    const tagObjects = [
      ...tags.map((tagSlug) => {
        const skill = allTags?.find((skill) => skill.slug === tagSlug)
        return { name: skill?.name || tagSlug }
      }),
      ...categories.map((categorySlug) => {
        const category = allCategories?.find((cat) => cat.slug === categorySlug)
        return { title: category?.title || categorySlug }
      }),
    ]

    document.title = generatePageTitle(tagObjects)

    // Update meta description for better SEO
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        generateMetaDescription(tagObjects)
      )
    }
  }

  const handleTagsChange = (newTags: string[]) => {
    console.info("handleTagsChange called with:", newTags)
    dispatchFilter({ type: "start-filter-change" })
    const skipTransition =
      newTags.length === 0 &&
      (hideCategories || selectedCategories.length === 0)
    updateUrlWithFilters(newTags, selectedCategories, undefined, {
      skipTransition,
    })
  }

  const handleCategoryChange = (newCategories: string[]) => {
    if (hideCategories) return

    console.info("handleCategoryChange called with:", newCategories)
    dispatchFilter({ type: "start-filter-change" })
    const skipTransition =
      newCategories.length === 0 && selectedTags.length === 0
    updateUrlWithFilters(selectedTags, newCategories, undefined, {
      skipTransition,
    })
  }

  const handleSortingChange = (newSortingType: string) => {
    console.info("handleSortingChange called with:", newSortingType)
    dispatchFilter({ type: "start-filter-change" })
    updateUrlWithFilters(selectedTags, selectedCategories, newSortingType)
  }

  const handleClearAllFilters = () => {
    console.info("Clearing all filters")
    dispatchFilter({ type: "start-filter-change" })
    updateUrlWithFilters([], defaultCategories, undefined, {
      skipTransition: true,
    })
  }

  // Filter posts based on selected tags and categories
  const filteredPosts = useMemo(() => {
    if (selectedTags.length === 0 && selectedCategories.length === 0) {
      return posts
    }

    return posts.filter((post) => {
      // Check if any selected tag matches post skills
      const matchesSkill =
        selectedTags.length === 0 ||
        post.skills?.some((skill: any) => selectedTags.includes(skill.slug))

      // Check if any selected category matches post categories
      const matchesCategory =
        selectedCategories.length === 0 ||
        post.category?.some((category: any) =>
          selectedCategories.includes(category.slug)
        )

      return matchesSkill && matchesCategory
    })
  }, [posts, selectedTags, selectedCategories])

  useEffect(() => {
    dispatchFilter({
      categories: hideCategories
        ? defaultCategories
        : initialSelectedCategories,
      sorting: initialSorting,
      tags: initialSelectedTags,
      type: "sync-from-url",
    })
  }, [
    defaultCategories,
    hideCategories,
    initialSelectedCategories,
    initialSelectedTags,
    initialSorting,
  ])

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search)
      const tagsFromUrl = urlParams.get("tags")
      const categoriesFromUrl = urlParams.get("categories")
      const sortFromUrl = urlParams.get("sort")

      const tags = tagsFromUrl ? tagsFromUrl.split(",").filter(Boolean) : []
      const categories = hideCategories
        ? defaultCategories
        : categoriesFromUrl
          ? categoriesFromUrl.split(",").filter(Boolean)
          : []
      const sorting = sortFromUrl || "recent"

      dispatchFilter({ categories, sorting, tags, type: "sync-from-url" })
    }

    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [defaultCategories, hideCategories])

  const sortedPosts = useMemo(() => {
    if (!filteredPosts) return []

    const sortedArray = [...filteredPosts]

    switch (sortingType) {
      case "recent":
        return sortedArray.sort(
          (a, b) =>
            new Date(b.date_created || b.date || "").getTime() -
            new Date(a.date_created || a.date || "").getTime()
        )
      case "oldest":
        return sortedArray.sort(
          (a, b) =>
            new Date(a.date_created || a.date || "").getTime() -
            new Date(b.date_created || b.date || "").getTime()
        )
      case "most-viewed":
        return sortedArray.sort((a, b) => (b.views || 0) - (a.views || 0))
      case "less-viewed":
        return sortedArray.sort((a, b) => (a.views || 0) - (b.views || 0))
      case "liked":
        return sortedArray.sort(
          (a, b) => (b.likes?.length || 0) - (a.likes?.length || 0)
        )
      case "least-liked":
        return sortedArray.sort(
          (a, b) => (a.likes?.length || 0) - (b.likes?.length || 0)
        )
      default:
        return sortedArray
    }
  }, [filteredPosts, sortingType])

  const hasActiveFilters =
    selectedTags.length > 0 ||
    (!hideCategories && selectedCategories.length > 0)
  const hasVisiblePosts = sortedPosts.length > 0
  const skeletonPostsLength = Math.max(
    1,
    Math.min(itemsPerPage, totalPosts || posts.length || 8)
  )
  const shouldShowSkeleton = isFilterChanging

  const startIndex = (currentPage - 1) * itemsPerPage
  const workItems: HomeWorkItem[] = useMemo(
    () =>
      sortedPosts.map((post: any, index: number) => {
        const date = dayjs(post.date_created || post.date)
        const firstCategoryTitle = post.category?.[0]?.title
        const displayNumber =
          typeof post.displayNumber === "number"
            ? post.displayNumber
            : startIndex + index + 1
        const eyebrow = `${String(displayNumber).padStart(2, "0")} / ${
          firstCategoryTitle ?? "Work"
        }`
        return {
          categories:
            post.category?.map((c: any) => ({
              id: c.id,
              slug: c.slug,
              title: c.title,
            })) ?? [],
          dateLabel: date.isValid() ? date.format("MMM YYYY") : undefined,
          evidenceMetric: post.evidence_metric ?? undefined,
          eyebrow,
          headline: post.headline ?? undefined,
          href: `${articlePathPrefix}/${post.slug}`,
          id: post.id ?? post.slug ?? `article-${index}`,
          image: post.image?.id ?? null,
          role: post.role ?? undefined,
          skills:
            post.skills?.reduce(
              (skills: NonNullable<HomeWorkItem["skills"]>, value: any) => {
                const skill = value?.skills_id ?? value

                if (!skill) {
                  return skills
                }

                skills.push({
                  id: skill.id,
                  name: skill.name,
                  slug: skill.slug,
                })

                return skills
              },
              [] as NonNullable<HomeWorkItem["skills"]>
            ) ?? [],
          stats: [],
          title: post.title ?? "",
        } satisfies HomeWorkItem
      }),
    [articlePathPrefix, sortedPosts, startIndex]
  )

  return (
    <Box
      as="section"
      borderLeft={"1px solid {colors.border}"}
      borderRight={"1px solid {colors.border}"}
      display="flex"
      flexDirection="column"
      flexGrow={1}
      flexShrink={0}
      gap={0}
      // gap={['2rem', '2rem', '4rem']}
      maxW="8xl"
      mx="auto"
      w={"full"}
    >
      <Box w="full">
        <WorkFilters
          allCategories={availableCategories}
          allTags={availableTags}
          hideCategories={hideCategories}
          onCategoryChange={handleCategoryChange}
          onClearAllFilters={handleClearAllFilters}
          onSortingChange={handleSortingChange}
          onTagsChange={handleTagsChange}
          selectedCategories={selectedCategories}
          selectedTags={selectedTags}
          sortedPostsLength={sortedPosts.length}
        />
      </Box>

      <Inner
        default="none"
        name="work-articles"
        update={workArticlesTransition}
      >
        {shouldShowSkeleton ? (
          <LoadingArticles postsLength={skeletonPostsLength} />
        ) : hasVisiblePosts ? (
          <LazyMotion features={domAnimation}>
            <Grid
              flexGrow={1}
              flexShrink={0}
              gap={{ base: 0 }}
              mb={0}
              templateColumns="1fr"
            >
              {workItems.map((item, index) => {
                const card = (
                  <FeaturedWorkCard
                    _first={{
                      borderBottom:
                        index < 1 ? "1px solid {colors.border}" : undefined,
                      borderTop: "0px solid {colors.border}",
                    }}
                    as={"div"}
                    bgColor={"bg.paper"}
                    borderTop="0px solid {colors.border}"
                    className="article"
                    imageProps={{
                      rounded: "0",
                    }}
                    index={index}
                    item={item}
                    priority={index < 2}
                    rounded={"0"}
                    showTags
                  />
                )

                return (
                  <Box
                    _first={{
                      "& .article": {
                        borderTop: "0px solid {colors.border}",
                      },
                    }}
                    _last={{
                      "& .article": {
                        borderBottom: "0px solid {colors.border}",
                      },
                    }}
                    _odd={{ "& .article-img": { order: "0" } }}
                    as={"article"}
                    asChild
                    key={item.id}
                  >
                    {isClientReady ? (
                      <m.div
                        animate={{ filter: "blur(0px)", opacity: 1, y: 0 }}
                        exit={{ filter: "blur(8px)", opacity: 0, y: -20 }}
                        initial={{ filter: "blur(12px)", opacity: 0, y: 20 }}
                        transition={{
                          delay: index * 0.04,
                          duration: 0.35,
                          ease: [0.215, 0.61, 0.355, 1],
                        }}
                      >
                        {card}
                      </m.div>
                    ) : (
                      <div>{card}</div>
                    )}
                  </Box>
                )
              })}
            </Grid>
          </LazyMotion>
        ) : (
          <VStack>
            <ErrorMessage
              message={
                hasActiveFilters
                  ? "Try selecting different filters or clear all filters."
                  : "Please try again."
              }
              status="error"
              title={
                hasActiveFilters
                  ? "No work articles found with selected filters!"
                  : "Unable to locate the work articles!"
              }
            />

            {hasActiveFilters ? (
              <Button
                colorPalette="primary"
                onClick={handleClearAllFilters}
                variant="solid"
              >
                Clear Filters
              </Button>
            ) : (
              <Button asChild colorPalette="primary" variant="solid">
                <NextLink href="/work" transitionTypes={["work-filter"]}>
                  Refresh
                </NextLink>
              </Button>
            )}
          </VStack>
        )}
      </Inner>
      <BlurReveal delay={0.12} style={{ width: "100%" }}>
        <Flex
          alignItems={["center", "center", "center"]}
          borderTop={{ base: "1px solid {colors.border}" }}
          flexDirection={["row", "row", "column"]}
          justifyContent={["space-between", "space-between", "center"]}
        >
          <PaginationBar
            currentPage={currentPage}
            onNavigateStart={() =>
              dispatchFilter({ type: "start-filter-change" })
            }
            pageSize={itemsPerPage}
            totalPages={totalPages}
            totalPosts={totalPosts}
          />
          {/* <ButtonPrimary>Show me more</ButtonPrimary> */}
        </Flex>
      </BlurReveal>
    </Box>
  )
}

const MyArticles: FC<MyArticlesProps> = (props) => (
  <MyArticlesContent {...props} />
)

export default MyArticles
